import type {
  Row,
  RowData,
  SortFn,
  TableFeatures,
} from "@tanstack/react-table";

import {
  constructSortFn,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
} from "@tanstack/react-table";

import { foldText } from "@/utils/text";

import type { TextFilterValue } from "./filter-toolbar/types";

/**
 * Compares a filter option key against a selected/row value. Multi-filter keys
 * are heterogeneous by design — `Object.entries` over a numeric-enum map yields
 * string keys ("1") while `uniqueValueOptions` and the row data keep the native
 * type (number 1) — so an exact compare alone would miss numeric-enum matches.
 * The string fallback bridges that gap. Used everywhere a key meets a value:
 * the filter fns here, the checkbox state, and the toggle add/remove logic.
 */
export function sameFilterValue(a: unknown, b: unknown): boolean {
  return a === b || String(a) === String(b);
}

// Written as generic functions rather than annotated `FilterFn<...>` consts:
// v9's FilterFn is invariant in its feature set, so annotating them would mean
// importing the very feature set that registers them.
export function enumIn<TFeatures extends TableFeatures, TData extends RowData>(
  row: Row<TFeatures, TData>,
  columnId: string,
  filterValue: unknown[],
): boolean {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  return filterValue.some((v) => sameFilterValue(v, value));
}

export function enumInArray<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  row: Row<TFeatures, TData>,
  columnId: string,
  filterValue: unknown[],
): boolean {
  if (!filterValue || filterValue.length === 0) return true;
  const value = row.getValue(columnId);
  if (!Array.isArray(value)) return false;
  return value.some((v) => filterValue.some((f) => sameFilterValue(f, v)));
}

export function textWithOperator<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  row: Row<TFeatures, TData>,
  columnId: string,
  filterValue: TextFilterValue,
): boolean {
  if (!filterValue || !filterValue.query) return true;
  const raw = row.getValue(columnId);
  if (raw == null) return false;
  const value = foldText(String(raw));
  const query = foldText(filterValue.query);
  switch (filterValue.operator) {
    case "equals":
      return value === query;
    case "starts_with":
      return value.startsWith(query);
    case "contains":
    default:
      return value.includes(query);
  }
}

/**
 * The built-in sort fns DataTable offers, keyed by the names `sortingFnForType`
 * picks from. Registered on the table as-is by `dataTableFeatures`, so a column
 * can only ask for a sort fn the table actually knows.
 */
export const BUILT_IN_SORT_FNS = {
  alphanumeric: sortFn_alphanumeric,
  basic: sortFn_basic,
  datetime: sortFn_datetime,
  // Array columns sort by item count. `alphanumeric` coerces anything that
  // isn't a string or number to "", which would leave every row tied.
  length: constructSortFn({
    ...sortFn_basic,
    resolveDataValue: (value) => (Array.isArray(value) ? value.length : 0),
  }),
};

export function sortingFnForType(
  type: string | undefined,
): keyof typeof BUILT_IN_SORT_FNS {
  if (type === "date" || type === "datetime") return "datetime";
  if (type === "number") return "basic";
  if (type === "array") return "length";
  return "alphanumeric";
}

/**
 * The sorting fn for a column, resolving its `sortTiebreakBy` when set.
 * Without one this is just the built-in name for the column's type. With one,
 * rows that compare equal on the column fall back to comparing that attribute
 * — otherwise TanStack settles full ties by original row index, which leaves
 * e.g. same-day date rows in API order, identically for both asc and desc.
 * Returning the tiebreak from the column's own comparator (rather than as a
 * second sort entry) means TanStack applies the column's direction to it too,
 * so descending by date also runs the tie newest-first.
 */
export function sortingFnFor<TFeatures extends TableFeatures, T>(
  type: string | undefined,
  tiebreakBy: keyof T | undefined,
): keyof typeof BUILT_IN_SORT_FNS | SortFn<TFeatures, T & RowData> {
  const builtIn = sortingFnForType(type);
  if (tiebreakBy == null) return builtIn;

  // v9 dropped the `sortingFns` registry object in favour of the individual
  // `sortFn_*` exports, which are also what `dataTableFeatures` registers.
  const compareColumn = BUILT_IN_SORT_FNS[builtIn];
  return (rowA, rowB, columnId) => {
    const byColumn = compareColumn(rowA, rowB, columnId);
    if (byColumn !== 0) return byColumn;
    // Bare relational compare, matching TanStack's own datetime/basic fns:
    // works for the ISO-string and numeric values these attributes hold, and
    // yields 0 for nullish ones so the tie just falls through to row order.
    const a = rowA.original[tiebreakBy] as string | number;
    const b = rowB.original[tiebreakBy] as string | number;
    return a > b ? 1 : a < b ? -1 : 0;
  };
}
